import cx from 'classnames'
import { useButton } from '@react-aria/button'
import { observer } from 'mobx-react-lite'
import { ChangeEvent, createContext, FC, useContext, useRef, useState, useEffect } from 'react'
import Collapse from 'react-css-collapse'
import { useRootStore } from 'stores/RootStore'
import { IThreadContext, ThreadProps } from './Types'
import { ReactComponent as CaretDown } from 'assets/icons/caretDown.svg'
import Styles from './Thread.module.scss'
import { useCollabItemContext } from 'components/CollabItem/CollabItem'
import { CountIndicator } from 'common/CountIndicator/CountIndicator'
import { Comment } from 'types'

const ThreadContext = createContext<IThreadContext>({
  renaming: false,
  setRenaming: () => {},
  collapsed: false,
})

const Title = observer(({ comment }: { comment: Comment }) => {
  const rootStore = useRootStore()
  const [newTitle, setNewTitle] = useState(comment.title)
  const { renaming, setRenaming } = useThreadContext()
  const inputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    setTimeout(() => {
      if (renaming) {
        inputRef.current?.focus()
      }
    }, 0)
  }, [renaming])

  const save = () => {
    rootStore.comments.update(comment.id, { title: newTitle }, [], false)
    setRenaming(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      setNewTitle(comment.title)
      setRenaming(false)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      save()
    }
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setNewTitle(e.target.value)
  }

  if (renaming) {
    return (
      <div className={Styles.title}>
        <input
          ref={inputRef}
          value={newTitle}
          onChange={handleChange}
          placeholder="Name this thread"
          onKeyDown={handleKeyDown}
          onBlur={save}
          className={Styles.titleInput}
        />
      </div>
    )
  }
  return <span className={Styles.title}>{comment.title}</span>
})

export const useThreadContext = () => useContext(ThreadContext)
const Thread: FC<React.PropsWithChildren<ThreadProps>> = observer(({ items, children }) => {
  const comment = items[0]
  const { title } = comment
  const rootStore = useRootStore()
  const { isHighlighted } = useCollabItemContext()
  const [collapsed, setCollapsed] = useState(false)
  const [renaming, setRenaming] = useState(false)
  const showHeader = title || renaming

  const toggleRef = useRef<HTMLButtonElement | null>(null)
  const { buttonProps: toggleButtonProps } = useButton(
    {
      onPress: () => setCollapsed((c) => !c),
    },
    toggleRef,
  )

  const headerRef = useRef<HTMLDivElement | null>(null)
  const { buttonProps: headerButtonProps } = useButton(
    {
      onPress: () => {
        rootStore.comments.select(comment.id)
        setCollapsed(false)
      },
      elementType: 'div',
    },
    headerRef,
  )

  return (
    <ThreadContext.Provider value={{ renaming, setRenaming, collapsed }}>
      {showHeader ? (
        <>
          <div
            className={cx(Styles.header, { [Styles.collapsed]: collapsed, [Styles.highlighted]: isHighlighted })}
            {...headerButtonProps}
          >
            <button className={Styles.toggle} {...toggleButtonProps} ref={toggleRef}>
              <CaretDown />
            </button>
            <Title comment={comment} />
            <CountIndicator count={items.length} />
          </div>
          <Collapse isOpen={!collapsed}>
            <div className={Styles.content}>{children}</div>
          </Collapse>
        </>
      ) : (
        children
      )}
    </ThreadContext.Provider>
  )
})

export { Thread }
